import React, { useState, useEffect, useRef } from "react";
import { motion, useSpring, useMotionValue, useTransform } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faMinus } from "@fortawesome/free-solid-svg-icons";
import gsap from "gsap";

const HolographicKPICard = ({
  title,
  value,
  previousValue,
  icon,
  prefix = "",
  suffix = "",
  format = "number",
  color = "cyan",
  className = "",
  animationDuration = 2000,
  size = "medium", // small, medium, large
  holographicIntensity = 1,
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef();
  const glowRef = useRef();
  const particleRef = useRef();

  // Mouse tracking for 3D effect
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // 3D transforms
  const rotateX = useTransform(mouseY, [-300, 300], [15, -15]);
  const rotateY = useTransform(mouseX, [-300, 300], [-15, 15]);
  const scale = useSpring(1);

  // Color schemes with holographic effects
  const colorSchemes = {
    cyan: {
      primary: "#00D9FF",
      secondary: "#0891b2",
      glow: "#00D9FF",
      gradient: "from-cyan-500/20 via-cyan-400/10 to-transparent",
      border: "border-cyan-400/30",
      shadow: "shadow-cyan-500/25",
      text: "text-cyan-100",
      iconBg: "bg-cyan-500/20",
      iconColor: "text-cyan-300",
    },
    purple: {
      primary: "#8B5CF6",
      secondary: "#7c3aed",
      glow: "#8B5CF6",
      gradient: "from-purple-500/20 via-purple-400/10 to-transparent",
      border: "border-purple-400/30",
      shadow: "shadow-purple-500/25",
      text: "text-purple-100",
      iconBg: "bg-purple-500/20",
      iconColor: "text-purple-300",
    },
    orange: {
      primary: "#FF6B35",
      secondary: "#ea580c",
      glow: "#FF6B35",
      gradient: "from-orange-500/20 via-orange-400/10 to-transparent",
      border: "border-orange-400/30",
      shadow: "shadow-orange-500/25",
      text: "text-orange-100",
      iconBg: "bg-orange-500/20",
      iconColor: "text-orange-300",
    },
    green: {
      primary: "#10B981",
      secondary: "#059669",
      glow: "#10B981",
      gradient: "from-emerald-500/20 via-emerald-400/10 to-transparent",
      border: "border-emerald-400/30",
      shadow: "shadow-emerald-500/25",
      text: "text-emerald-100",
      iconBg: "bg-emerald-500/20",
      iconColor: "text-emerald-300",
    },
  };

  const scheme = colorSchemes[color] || colorSchemes.cyan;

  // Size variants
  const sizeClasses = {
    small: "p-4 h-40 min-h-[10rem]",
    medium: "p-6 h-48 min-h-[12rem]",
    large: "p-8 h-56 min-h-[14rem]",
  };

  const textSizes = {
    small: "text-2xl",
    medium: "text-3xl",
    large: "text-4xl",
  };

  const iconSizes = {
    small: "text-lg p-2",
    medium: "text-xl p-3",
    large: "text-2xl p-4",
  };

  // Liquid animation for the counter
  useEffect(() => {
    if (value === undefined || value === null) return;

    const startValue = 0;
    const endValue = parseFloat(value) || 0;
    const duration = animationDuration;

    gsap.to({ value: startValue }, {
      duration: duration / 1000,
      value: endValue,
      ease: "power3.out",
      onUpdate: function () {
        setDisplayValue(this.targets()[0].value);
      },
      onComplete: () => {
        setIsLoaded(true);
        // Particle burst effect on completion
        if (particleRef.current) {
          gsap.fromTo(particleRef.current.children,
            { scale: 0, opacity: 1 },
            {
              scale: 1.5,
              opacity: 0,
              duration: 1,
              stagger: 0.1,
              ease: "power2.out"
            }
          );
        }
      }
    });
  }, [value, animationDuration]);

  // Hover animations
  useEffect(() => {
    if (isHovered) {
      scale.set(1.05);
      // Glow pulse animation
      if (glowRef.current) {
        gsap.to(glowRef.current, {
          opacity: 0.8,
          duration: 0.3,
          ease: "power2.out"
        });
      }
    } else {
      scale.set(1);
      if (glowRef.current) {
        gsap.to(glowRef.current, {
          opacity: 0.3,
          duration: 0.3,
          ease: "power2.out"
        });
      }
    }
  }, [isHovered, scale]);

  // Mouse tracking
  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    mouseX.set(event.clientX - centerX);
    mouseY.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  };

  // Format the display value
  const formatValue = (val) => {
    if (format === "currency") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(val);
    } else if (format === "percentage") {
      return `${val.toFixed(1)}%`;
    } else if (format === "decimal") {
      return val.toFixed(2);
    } else {
      return Math.round(val).toLocaleString();
    }
  };

  // Calculate percentage change
  const calculateChange = () => {
    if (!previousValue || previousValue === 0) return { value: 0, type: "neutral" };

    const change = ((value - previousValue) / previousValue) * 100;
    const type = change > 0 ? "positive" : change < 0 ? "negative" : "neutral";

    return { value: Math.abs(change), type };
  };

  const change = calculateChange();

  const getChangeIcon = () => {
    switch (change.type) {
      case "positive":
        return faArrowUp;
      case "negative":
        return faArrowDown;
      default:
        return faMinus;
    }
  };

  const getChangeColor = () => {
    switch (change.type) {
      case "positive":
        return "text-emerald-400 bg-emerald-500/20";
      case "negative":
        return "text-red-400 bg-red-500/20";
      default:
        return "text-gray-400 bg-gray-500/20";
    }
  };

  return (
    <motion.div
      ref={cardRef}
      className={`relative overflow-hidden cursor-pointer ${sizeClasses[size]} ${className}`}
      style={{
        rotateX,
        rotateY,
        scale,
        transformStyle: "preserve-3d",
        perspective: 1000,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      whileHover={{ z: 50 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      {/* Holographic glow effect */}
      <div
        ref={glowRef}
        className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${scheme.gradient} opacity-30 blur-xl`}
        style={{
          filter: `blur(20px) brightness(${holographicIntensity})`,
        }}
      />

      {/* Main card with glassmorphism */}
      <div className={`relative h-full rounded-2xl backdrop-blur-xl bg-white/5 ${scheme.border} border ${scheme.shadow} shadow-2xl`}>
        {/* Chromatic aberration effect */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/5 via-transparent to-blue-500/5 mix-blend-screen" />

        {/* Card content */}
        <div className="relative h-full p-6 flex flex-col justify-between">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <motion.div
              className={`${scheme.iconBg} ${iconSizes[size]} rounded-xl backdrop-blur-sm`}
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              <FontAwesomeIcon
                icon={icon}
                className={scheme.iconColor}
              />
            </motion.div>

            {/* Change Indicator */}
            {previousValue && (
              <motion.div
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getChangeColor()}`}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1, type: "spring" }}
              >
                <FontAwesomeIcon
                  icon={getChangeIcon()}
                  className="text-xs"
                />
                <span>{change.value.toFixed(1)}%</span>
              </motion.div>
            )}
          </div>

          {/* Value */}
          <div className="mb-2">
            <motion.div
              className={`${textSizes[size]} font-bold ${scheme.text} transition-all duration-500`}
              style={{
                filter: isLoaded ? 'none' : 'blur(4px)',
                textShadow: `0 0 20px ${scheme.glow}40`,
              }}
            >
              {prefix}
              {formatValue(displayValue)}
              {suffix}
            </motion.div>
          </div>

          {/* Title */}
          <div className="text-sm font-medium text-gray-300 uppercase tracking-wider">
            {title}
          </div>

          {/* Particle effects container */}
          <div ref={particleRef} className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className={`absolute w-2 h-2 rounded-full ${scheme.iconBg} opacity-0`}
                style={{
                  left: `${20 + (i % 4) * 20}%`,
                  top: `${30 + Math.floor(i / 4) * 40}%`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Holographic scan line effect */}
        <motion.div
          className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"
          animate={{
            y: [0, 200, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Edge glow */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${scheme.gradient} opacity-20`} />

        {/* Corner accents */}
        <div className={`absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 ${scheme.border} rounded-tl-lg`} />
        <div className={`absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 ${scheme.border} rounded-tr-lg`} />
        <div className={`absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 ${scheme.border} rounded-bl-lg`} />
        <div className={`absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 ${scheme.border} rounded-br-lg`} />
      </div>

      {/* Reflection effect */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/10 via-transparent to-transparent pointer-events-none" />
    </motion.div>
  );
};

export default HolographicKPICard;